import React, { useEffect, useRef, useState } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { format } from 'date-fns'
import { useThemeMode } from '../../../../../_metronic/partials'
import { useAuth } from '../../../auth'
import { getTemplates } from '../../core/_requests'
import { toast } from 'react-toastify'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'
import { NoDataPage } from '../../../resources/components/NoDataPage'
import GenerateReportModal from './GenerateReportModal'
import TemplateReportModal from './TemplateReportModal'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

interface ReportsTemplate {
  byUser: string
  role: string
  id: number
  name: string
  dateTime: string
  generatedBy: string
}

export const ReportsTemplateTable = ({
  setTabKey,
  isActive,
  setIsActive,
  selectedReportTypeFilter,
  search,
}: {
  search: string
  setTabKey: any
  isActive: any
  setIsActive: any
  selectedReportTypeFilter: string
}) => {
  const { formatMessage } = useIntl()
  const { companyId } = useAuth()
  const { mode } = useThemeMode()
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [, setShowKebabMenu] = useState(false)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [templateId, setTemplateId] = useState<string>('')
  const [active, setActive] = useState<boolean>(false)

  // const [filter, setFilter] = useState(false) //eslint-disable-line
  // const handleFilter = () => {
  //     setFilter(true)
  // }
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }

  useEffect(() => {
    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside)

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const [data, setData] = useState<ReportsTemplate[]>([])
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'reportName',
        header: 'Templete Name',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'generatedBy',
        id: 'generatedBy',
        header: 'Created By',
        cell: (info) => {
          const { user, role } = info.row.original
          return (
            <div className='d-flex align-items-center gap-2'>
              <div>
                <h5 className='m-0 font-size-13 fw-normal'>
                  {user?.firstName} {user?.lastName}
                </h5>
                <span className='role-owner-color'>{role}</span>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },

      {
        accessorKey: 'reportType',
        id: 'reportType',
        header: 'Report Type',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'updatedAt',
        id: 'updatedAt',
        header: 'Last Updated on',
        cell: (info) => {
          const rawDate = info.getValue()
          // Format the date using date-fns
          const formattedDate = rawDate ? format(new Date(rawDate), 'dd MMM, yyyy hh:mm a') : 'N/A'
          return <span>{formattedDate}</span>
        },
        filterFn: 'includesString',
      },
    ],
    []
  )

  const getAllTemplates = async () => {
    if (!companyId) return
    setLoading(true)
    const type = 'Template'
    const all = false
    try {
      const {
        data: { data, success, errors },
      } = await getTemplates(
        companyId,
        type,
        all,
        pagination.currentPage,
        pagination.pageSize,
        selectedReportTypeFilter,
        search
      )
      if (success) {
        setData(data?.data)
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination.currentPage,
          pageSize: data.pagination.pageSize,
          totalCount: data.pagination.totalRecords,
          nextPage: data.pagination.nextPage,
          prevPage: data.pagination.prevPage,
          totalPages: data.pagination.totalPages,
        }))
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAllTemplates()
  }, [companyId, pagination.currentPage, pagination.pageSize, search, selectedReportTypeFilter, isActive]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Function to handle clicks outside of the dropdown menu
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowKebabMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  return (
    <>
      <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
        {!isLoading && (data?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage />
          </div>
        ) : (
          <Table className='w-100' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan} className='min-w-120px pb-6 pt-4'>
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4 text-end'>
                    {formatMessage({ id: 'Actions' })}
                  </th>
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr key={row.id} className='border-bottom'>
                    {row.getVisibleCells().map((cell, index) => (
                      <>
                        <td key={cell.id} className='pt-5 border-0'>
                          <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                        </td>
                      </>
                    ))}
                    <td
                      className='text-end'
                      onClick={() => {
                        const templateId = row.original.recordTemplateId
                        setTemplateId(templateId)
                        setActive(true)
                      }}
                    >
                      <button
                        className='btn border border-primary font-size-13 text-primary py-2'
                        style={
                          mode === `dark` ? { background: 'transparent' } : { background: '#F0F4FF' }
                        }
                      >
                        {formatMessage({ id: 'Use This Templete' })}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

      <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom'>
        <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-md-0 mb-4'>
          <select
            aria-label='Default select example'
            className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark '
            style={{ outline: 'none' }}
            value={pagination.pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value)
              setPagination((prev) => ({
                ...prev,
                pageSize: newSize,
                pageIndex: 0, // Reset to first page when page size changes
              }))
            }}
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>

          <span className='font-size-13 text-center text-md-start'>
            {formatMessage({
              id: `Showing ${pagination.currentPage === 1
                ? 1
                : (pagination.currentPage - 1) * pagination.pageSize + 1
                } to ${pagination.nextPage ? data.length : pagination.totalCount} of ${pagination.totalCount
                } records`,
            })}
          </span>
        </div>

        {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          <Pagination.First
            onClick={() => handlePageChange(0)}
            disabled={pagination.currentPage === 1}
          >
            {'<<'}
          </Pagination.First>

          <Pagination.Prev
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.prevPage}
          >
            {'<'}
          </Pagination.Prev>

          {Array.from({ length: pagination.totalPages }, (_, i) => (
            <Pagination.Item
              key={i}
              active={i === pagination.currentPage - 1}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.nextPage}
          >
            {'>'}
          </Pagination.Next>

          <Pagination.Last
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={!pagination.nextPage}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination> */}
        <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          {/* First Page Button */}
          <Pagination.First
            onClick={() => handlePageChange(0)}
            disabled={pagination.currentPage === 1}
          >
            {'<<'}
          </Pagination.First>

          {/* Previous Page Button */}
          <Pagination.Prev
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
          >
            {'<'}
          </Pagination.Prev>

          {/* Page Number Buttons */}
          {Array.from({ length: pagination.totalPages }, (_, i) => (
            <Pagination.Item
              key={i}
              active={i === pagination.currentPage - 1}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}

          {/* Next Page Button */}
          <Pagination.Next
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage >= pagination.totalPages}
          >
            {'>'}
          </Pagination.Next>

          {/* Last Page Button */}
          <Pagination.Last
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.currentPage >= pagination.totalPages}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination>

        <GenerateReportModal
          templateId={templateId}
          isActive={active}
          setIsActive={setActive}
          setTabKey={setTabKey}
        />
        <TemplateReportModal
          isActive={isActive}
          setIsActive={setIsActive}
          getAllTemplates={getAllTemplates}
          setTabKey={setTabKey}
        />
      </div>
    </>
  )
}

// function DebouncedInput({
//     value: initialValue,
//     onChange,
//     debounce = 500,
//     ...props
// }: {
//     value: string | number;
//     onChange: (value: string | number) => void;
//     debounce?: number;
// } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
//     const [value, setValue] = useState(initialValue);

//     useEffect(() => {
//         setValue(initialValue);
//     }, [initialValue]);

//     useEffect(() => {
//         const timeout = setTimeout(() => {
//             onChange(value);
//         }, debounce);

//         return () => clearTimeout(timeout);
//     }, [value, debounce, onChange]);
//     return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />;
// }
